import {
    Box,
    Accordion,
    AccordionItem,
    AccordionButton,
    AccordionPanel,
    AccordionIcon,
    Text,
    Button,
    Image,
    useMediaQuery
} from '@chakra-ui/react'
import { Department } from '@core/app/types/contactsApiType'

interface AsideDropdownProps {
    countries: {
        name: string
        flag: string | null
        departments: { [key: number]: Department }
    }[]
    selectedDepartment: Department | null
    onSelectDepartment: (department: Department | null) => void
    selectedOnly?: boolean
    onAccordionButtonClick?: () => void
    onAccordionPanelClick?: () => void
}

const AsideDropdown = ({
    countries,
    selectedDepartment,
    onSelectDepartment,
    selectedOnly,
    onAccordionButtonClick,
    onAccordionPanelClick
}: AsideDropdownProps) => {
    const getFontWeight = (isSelected: boolean) => isSelected ? "600" : "500"
    const getColor = (isSelected: boolean) => isSelected ? "#2F80ED" : "#1A1A1A"

    const [isDesktop] = useMediaQuery('(min-width: 1230px)', {
        fallback: false,
    })

    const filteredCountries = selectedOnly && selectedDepartment
        ? countries.filter(country =>
            Object.values(country.departments).some(dept => dept.name === selectedDepartment.name)
          )
        : countries

    return (
        <Accordion allowMultiple defaultIndex={[0]} gap={5}>
            {filteredCountries.map(country => {
                const isSelectedCountry = selectedDepartment?.department.country === country.name
                const departments = Object.values(country.departments)
                const visibleDepartments = selectedOnly && selectedDepartment
                    ? departments.filter(dept => dept.name === selectedDepartment.name)
                    : departments

                const hasDepartmentsWithCountry = visibleDepartments.some(
                    dept => dept.department && dept.department.country
                )

                const isSelectedDept = !!(selectedDepartment && departments.some(dept => dept.name === selectedDepartment.name));
                const firstDept = departments[0]

                return (
                    <AccordionItem border="none" key={country.name} mb={4}>
                        <AccordionButton p={0} lineHeight={2} onClick={onAccordionButtonClick}>
                            {hasDepartmentsWithCountry ? (
                                <Box flex="1" textAlign="left" display="flex" alignItems="center">
                                    {country.flag && (
                                        <Image
                                            alt={country.name}
                                            boxSize="24px"
                                            mr="2"
                                            src={
                                                process.env.ENV === 'local'
                                                    ? `${process.env.API_URL}/${country.flag}`
                                                    : country.flag
                                            }
                                        />
                                    )}
                                    <Text
                                        fontSize="sm"
                                        fontWeight={getFontWeight(isSelectedCountry)}
                                        color={getColor(isSelectedCountry)}
                                    >
                                        {country.name}
                                    </Text>
                                </Box>
                            ) : (
                                <Box
                                    flex="1" 
                                    pl={0}
                                    mb={4}
                                    height="12px"
                                    textAlign="left"
                                    onClick={() => {
                                        if (firstDept) {
                                            onSelectDepartment(firstDept)
                                            onAccordionPanelClick && onAccordionPanelClick()
                                        } else {
                                            console.warn('First department not found');
                                        }
                                    }}
                                >
                                    <Text 
                                        fontSize="sm"
                                        color={getColor(isSelectedDept)}
                                        fontWeight={getFontWeight(isSelectedDept)}
                                    >
                                        {country.name}
                                    </Text>
                                </Box>
                            )}
                            {(!hasDepartmentsWithCountry && isDesktop) || (!selectedOnly && !isDesktop && !hasDepartmentsWithCountry) ? null : (
                                <AccordionIcon style={selectedOnly ? { transform: 'rotate(0deg)' } : {}} />
                            )}
                        </AccordionButton>
                        {!selectedOnly && hasDepartmentsWithCountry && (
                            visibleDepartments.map(dept => {
                                const isSelectedDept = selectedDepartment?.name === dept.name
                                const displayLocation = dept.department.city || dept.name
                                return (
                                    <AccordionPanel 
                                        p={0}
                                        maxWidth={isDesktop ? "196px" : "100%"}
                                        key={dept.department.departmentId} 
                                        onClick={onAccordionPanelClick}
                                    >
                                        <Button
                                            pb={1} 
                                            pt={2} 
                                            pl={8}
                                            fontSize="sm"
                                            variant="link"
                                            textAlign="left"
                                            whiteSpace="normal"
                                            justifyContent="start"
                                            color={getColor(isSelectedDept)}
                                            fontWeight={getFontWeight(isSelectedDept)}
                                            onClick={() => {
                                                if (dept) {
                                                    onSelectDepartment(dept)
                                                } else {
                                                    console.warn('Department not found');
                                                }
                                            }}
                                        >
                                            {displayLocation}
                                        </Button>
                                    </AccordionPanel>
                                )
                            })
                        )}
                    </AccordionItem>
                )
            })}
        </Accordion>
    )
}

export default AsideDropdown
